import React, { FC } from 'react';

import { config, yup } from 'data';
import { paymentService } from 'services';
import { useForm, useLang, useMutation } from 'hooks';
import { CountrySelect } from 'components/selects';
import { Alert, Form, Input, Modal } from 'components/ui';
import { Nullish } from 'types/common';
import { ModalBaseProps } from 'types/components';
import { PaymentCardAvsVerificationParams } from 'types/services';

type FormValues = PaymentCardAvsVerificationParams;

const initialValues: FormValues = {
  country: config.DEFAULT_COUNTRY,
  state: '',
  city: '',
  address: '',
  zipcode: '',
};

const validationSchema = yup.object({
  country: yup.string().required().country(),
  state: yup.string().required().min(config.STATE_MIN_LENGTH).max(config.STATE_MAX_LENGTH),
  city: yup.string().required().min(config.CITY_MIN_LENGTH).max(config.CITY_MAX_LENGTH),
  address: yup.string().required().min(config.ADDRESS_MIN_LENGTH).max(config.ADDRESS_MAX_LENGTH),
  zipcode: yup.string().required().max(config.ZIP_MAX_LENGTH),
});

type AvsVerificationModalProps = ModalBaseProps & {
  trxRef: string;
  error?: Nullish<string>;
  pending?: boolean;
  onSuccess?: VoidFunction;
};

const AvsVerificationModal: FC<AvsVerificationModalProps> = ({
  trxRef,
  error,
  pending,
  onSuccess,
  ...props
}) => {
  const form = useForm<FormValues>();
  const lang = useLang();

  const verifyCardPaymentAvsMutation = useMutation({
    mutationFn: (values: FormValues) => paymentService.verifyCardPaymentAvs(trxRef, values),
    onSuccess,
  });

  const handleSubmit = async (values: FormValues) => {
    await verifyCardPaymentAvsMutation.mutateAsync(values);

    return false;
  };

  const loading = pending || verifyCardPaymentAvsMutation.isPending;

  return (
    <Modal
      title={lang.get('invoicePayment.avsModal.title')}
      caption={lang.get('invoicePayment.avsModal.caption')}
      okText={lang.get('common.actions.continue')}
      okButtonProps={{ block: true }}
      cancelButtonProps={{ hidden: true }}
      confirmLoading={loading}
      closable={false}
      maskClosable={false}
      onOk={form.submit}
      {...props}
    >
      <Form
        form={form}
        initialValues={initialValues}
        validationSchema={validationSchema}
        disabled={loading}
        onFinish={handleSubmit}
      >
        <Form.Item hidden={!error}>
          <Alert type="error" message={error} />
        </Form.Item>
        <Form.Item name="country" label={lang.get('common.form.country.label')}>
          <CountrySelect />
        </Form.Item>
        <Form.Columns>
          <Form.Item name="state" label={lang.get('common.form.state.label')}>
            <Input placeholder={lang.get('common.form.state.placeholder')} />
          </Form.Item>
          <Form.Item name="city" label={lang.get('common.form.city.label')}>
            <Input placeholder={lang.get('common.form.city.placeholder')} />
          </Form.Item>
        </Form.Columns>
        <Form.Item name="address" label={lang.get('common.form.address.label')}>
          <Input placeholder={lang.get('common.form.address.placeholder')} />
        </Form.Item>
        <Form.Item name="zipcode" label={lang.get('common.form.zip.label')}>
          <Input placeholder={lang.get('common.form.zip.placeholder')} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AvsVerificationModal;
